'use client';
import React, { useEffect, useRef } from 'react';

interface Feature {
  icon: string;
  title: string;
  description: string;
  tag: string;
  accent: string;
  size: string;
}

const features: Feature[] = [
  {
    icon: '⚡',
    title: 'AI UGC Ads Workflows',
    description: 'End-to-end prompt systems that produce scroll-stopping UGC-style ads — complete with hooks, voiceovers, and visual direction.',
    tag: 'Core System',
    accent: 'from-violet-500/20 to-purple-600/10',
    size: 'md:col-span-2',
  },
  {
    icon: '🧬',
    title: 'Creator Realism Systems',
    description: 'Advanced AI persona frameworks that make generated content indistinguishable from real creator footage.',
    tag: 'Realism Engine',
    accent: 'from-pink-500/20 to-rose-600/10',
    size: 'md:col-span-1',
  },
  {
    icon: '🔥',
    title: 'Viral Hook Frameworks',
    description: 'Proven hook structures engineered for maximum thumb-stop rate across TikTok, Reels, and Shorts.',
    tag: 'Conversion',
    accent: 'from-orange-500/20 to-amber-600/10',
    size: 'md:col-span-1',
  },
  {
    icon: '🤖',
    title: 'AI Influencer Workflows',
    description: 'Build complete AI influencer personas with consistent visual identity, voice, and content style for any niche.',
    tag: 'Influencer',
    accent: 'from-cyan-500/20 to-teal-600/10',
    size: 'md:col-span-1',
  },
  {
    icon: '💎',
    title: 'Luxury Brand Campaign Systems',
    description: 'Premium campaign frameworks for high-end brands — cinematic product reveals, editorial aesthetics, and aspirational storytelling.',
    tag: 'Premium',
    accent: 'from-yellow-500/20 to-amber-600/10',
    size: 'md:col-span-2',
  },
  {
    icon: '🔄',
    title: 'Future Updates Included',
    description: 'Every new workflow, system, and framework we release is automatically added to your library — forever.',
    tag: 'Lifetime',
    accent: 'from-emerald-500/20 to-green-600/10',
    size: 'md:col-span-1',
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('ugc-reveal-active');
          }
        });
      },
      { threshold: 0.1 }
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="relative bg-[#080810] py-28 px-6 md:px-12">
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/20 bg-violet-500/5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="font-sans text-xs text-violet-300 tracking-wide">Everything You Need</span>
          </div>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-white leading-tight mb-4">
            Six systems. One studio.
          </h2>
          <p className="font-sans text-lg text-white/40 leading-relaxed">
            Every workflow you need to produce professional AI UGC content — from concept to campaign-ready.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((feature, i) => (
            <div
              key={feature.title}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`ugc-reveal group relative rounded-2xl border border-white/8 bg-gradient-to-br ${feature.accent} p-6 md:p-8 cursor-default overflow-hidden transition-all duration-500 hover:border-white/15 hover:-translate-y-1 ${feature.size}`}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(circle at 50% 0%, rgba(139,92,246,0.08) 0%, transparent 60%)' }} />

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-5">
                  <div className="text-3xl">{feature.icon}</div>
                  <span className="font-sans text-[10px] text-white/30 tracking-widest uppercase border border-white/10 rounded-full px-2.5 py-1">
                    {feature.tag}
                  </span>
                </div>
                <h3 className="font-sans font-semibold text-lg text-white mb-3 leading-snug">
                  {feature.title}
                </h3>
                <p className="font-sans text-sm text-white/45 leading-relaxed">
                  {feature.description}
                </p>
              </div>

              {/* Corner accent */}
              <div className="absolute bottom-0 right-0 w-24 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(circle at 100% 100%, rgba(139,92,246,0.15) 0%, transparent 70%)' }} />
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .ugc-reveal {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.5s, translate 0.3s;
        }
        .ugc-reveal-active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}
